import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import { DirtyNavigationGuardProvider } from './navigationGuardContext';
import { AppShell } from './AppShell';
import { ErrorState, ScreenLoader } from '../components/States';
import { useClient } from '../data/hooks';
import Reports from '../screens/Reports';

const Login = lazy(() => import('../screens/Login'));
const Dashboard = lazy(() => import('../screens/Dashboard'));
const Clients = lazy(() => import('../screens/Clients'));
const NewClient = lazy(() => import('../screens/NewClient'));
const ClientWorkspace = lazy(() => import('../screens/ClientWorkspace'));
const ClientDetails = lazy(() => import('../screens/ClientDetails'));
const Plan = lazy(() => import('../screens/Plan'));
const Scenario = lazy(() => import('../screens/Scenario'));
const Goals = lazy(() => import('../screens/Goals'));
const Contributions = lazy(() => import('../screens/Contributions'));
const ClientReport = lazy(() => import('../screens/ClientReport'));
const FollowUp = lazy(() => import('../screens/FollowUp'));
const BalanceSheet = lazy(() => import('../screens/BalanceSheet'));
const Protection = lazy(() => import('../screens/Protection'));
const PrivatePension = lazy(() => import('../screens/PrivatePension'));
const FinancingComparison = lazy(() => import('../screens/FinancingComparison'));
const Family = lazy(() => import('../screens/Family'));
const ClientFiles = lazy(() => import('../screens/ClientFiles'));
const Settings = lazy(() => import('../screens/Settings'));
const ReviewData = lazy(() => import('../screens/ReviewData'));
const NotFound = lazy(() => import('../screens/NotFound'));

export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { session, loading } = useAuth();
  const location = useLocation();
  if (loading) return <ScreenLoader />;
  if (!session) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return children;
}

export function PromotedClientRouteGuard() {
  const { clientId = '' } = useParams();
  const clientQuery = useClient(clientId);
  if (clientQuery.isPending) return <ScreenLoader />;
  if (clientQuery.isError) return <ErrorState description="Não foi possível carregar este cliente." retry={() => { void clientQuery.refetch(); }} />;
  if (!clientQuery.data) return <Navigate to="/clientes" replace />;
  if (clientQuery.data.dataState === 'candidate-only') return <Navigate to={`/clientes/${clientId}`} replace />;
  return <Outlet />;
}

function AppRoutes() {
  return (
    <Suspense fallback={<ScreenLoader />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
          <Route path="/" element={<Dashboard />} />
          <Route path="/clientes" element={<Clients />} />
          <Route path="/clientes/novo" element={<NewClient />} />
          <Route path="/clientes/:clientId" element={<ClientWorkspace />} />
          <Route path="/clientes/:clientId/dados" element={<ClientDetails />} />
          <Route element={<PromotedClientRouteGuard />}>
            <Route path="/clientes/:clientId/plano" element={<Plan />} />
            <Route path="/clientes/:clientId/cenarios/:scenarioId" element={<Scenario />} />
            <Route path="/clientes/:clientId/metas" element={<Goals />} />
            <Route path="/clientes/:clientId/aportes" element={<Contributions />} />
            <Route path="/clientes/:clientId/patrimonio" element={<BalanceSheet />} />
            <Route path="/clientes/:clientId/protecao" element={<Protection />} />
            <Route path="/clientes/:clientId/previdencia" element={<PrivatePension />} />
            <Route path="/clientes/:clientId/financiamentos" element={<FinancingComparison />} />
            <Route path="/clientes/:clientId/relatorio" element={<ClientReport />} />
          </Route>
          <Route path="/clientes/:clientId/familia" element={<Family />} />
          <Route path="/clientes/:clientId/arquivos" element={<ClientFiles />} />
          <Route path="/relatorios" element={<Reports />} />
          <Route path="/acompanhamento" element={<FollowUp />} />
          <Route path="/revisao" element={<ReviewData />} />
          <Route path="/configuracoes" element={<Settings />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return <AuthProvider><DirtyNavigationGuardProvider><AppRoutes /></DirtyNavigationGuardProvider></AuthProvider>;
}
